import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Activity, Target, TrendingUp } from 'lucide-react';
import { useSurgeIntelligence } from '../../hooks/useSurgeIntelligence';
import type { SurgeEpisode, CausalFactor, PredictiveTemplate } from '../../types/calculations';

interface SurgeIntelligenceCardProps {
  submarketName: string | undefined;
}

const strengthColors: Record<string, string> = {
  strong: 'bg-emerald/15 text-emerald border-emerald/30',
  moderate: 'bg-gold/15 text-gold border-gold/30',
  weak: 'bg-stone-700/50 text-stone-700 border-stone-600',
};

function EvidenceBadge({ factor }: { factor: CausalFactor }) {
  const cls = strengthColors[factor.strength] || strengthColors.weak;
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold uppercase tracking-wider border ${cls}`}>
      {factor.layer.replace('_', ' ')}
      <span className="opacity-60">({factor.strength})</span>
    </div>
  );
}

function EpisodeCard({ episode }: { episode: SurgeEpisode }) {
  const [expanded, setExpanded] = useState(false);
  const isOngoing = episode.period_end === 'present';

  return (
    <div className="bg-graphite border border-slate-dark rounded-lg p-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-ivory">{episode.submarket}</span>
            {isOngoing && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-sm font-bold uppercase tracking-wider bg-gold/20 text-gold border border-gold/40 animate-pulse">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-stone-700">
            <span>{episode.period_start} - {episode.period_end}</span>
            <span className="text-gold font-mono tabular-nums font-semibold">+{episode.peak_growth_pct}% peak</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-stone-700" /> : <ChevronDown className="w-4 h-4 text-stone-700" />}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-dark space-y-3">
          <div>
            <div className="text-sm uppercase tracking-wider text-stone-700 mb-1.5">Evidence Layers</div>
            <div className="flex flex-wrap gap-1.5">
              {episode.causal_factors.map((cf, i) => (
                <EvidenceBadge key={i} factor={cf} />
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Key Question</div>
            <p className="text-sm text-stone-300 italic">{episode.key_question}</p>
          </div>

          {episode.causal_hypothesis_summary && (
            <div>
              <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Causal Hypothesis</div>
              <p className="text-sm text-stone-300 leading-relaxed">{episode.causal_hypothesis_summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateSection({ template }: { template: PredictiveTemplate }) {
  return (
    <div className="bg-graphite border border-gold/20 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-gold" />
        <span className="text-sm font-semibold text-gold">{template.pattern_name}</span>
      </div>

      <div className="space-y-3">
        <div>
          <div className="text-sm uppercase tracking-wider text-stone-700 mb-1.5">Preconditions</div>
          <ul className="space-y-1">
            {template.preconditions.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-300">
                <span className="text-gold mt-0.5">&#9656;</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-charcoal rounded p-2 border border-slate-dark">
            <div className="text-sm uppercase tracking-wider text-stone-700">Timeline</div>
            <div className="text-sm font-mono tabular-nums text-ivory mt-0.5">{template.expected_timeline}</div>
          </div>
          <div className="bg-charcoal rounded p-2 border border-slate-dark">
            <div className="text-sm uppercase tracking-wider text-stone-700">Magnitude</div>
            <div className="text-sm font-mono tabular-nums text-ivory mt-0.5">{template.expected_magnitude}</div>
          </div>
          <div className="bg-charcoal rounded p-2 border border-slate-dark">
            <div className="text-sm uppercase tracking-wider text-stone-700">Duration</div>
            <div className="text-sm font-mono tabular-nums text-ivory mt-0.5">{template.expected_duration}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-sm font-semibold uppercase tracking-wider bg-gold/15 text-gold border border-gold/30">
            {template.confidence.split(' ')[0]}
          </span>
        </div>

        <div className="p-2 bg-gold/5 border border-gold/15 rounded text-sm text-stone-300">
          <span className="text-gold font-semibold">Watch for:</span> {template.watch_for}
        </div>
      </div>
    </div>
  );
}

export function SurgeIntelligenceCard({ submarketName }: SurgeIntelligenceCardProps) {
  const { data, isLoading } = useSurgeIntelligence(submarketName);

  if (!submarketName || isLoading) return null;
  if (!data) return null;

  const hasContent = data.episodes.length > 0 || data.precursor || data.matched_template;
  if (!hasContent) return null;

  return (
    <div className="bg-charcoal border border-slate-dark rounded-xl p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-gold text-lg tracking-wide">Surge Intelligence</h3>
      </div>

      {/* Active Surge Banner */}
      {data.is_active_surge && data.episodes.length > 0 && (() => {
        const active = data.episodes.find(e => e.period_end === 'present');
        if (!active) return null;
        return (
          <div className="p-3 bg-gold/10 border border-gold/40 rounded-lg flex items-center gap-3">
            <Activity className="w-5 h-5 text-gold shrink-0" />
            <div>
              <div className="text-sm font-semibold text-gold">Active Surge Detected</div>
              <div className="text-sm text-stone-300">
                {active.submarket} since {active.period_start} — peak {active.peak_growth_pct}% YoY growth
              </div>
            </div>
          </div>
        );
      })()}

      {/* Episode Cards */}
      {data.episodes.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm uppercase tracking-wider text-stone-700">Historical Episodes</div>
          {data.episodes.map((ep, i) => (
            <EpisodeCard key={i} episode={ep} />
          ))}
        </div>
      )}

      {/* Predictive Template */}
      {data.matched_template && (
        <TemplateSection template={data.matched_template} />
      )}

      {/* Precursor Alert */}
      {data.precursor && (
        <div className="p-4 bg-amber-900/15 border border-amber-600/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-400">Precursor Alert</span>
            <span className={`ml-auto inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold uppercase tracking-wider border ${
              data.precursor.signal === 'BUY'
                ? 'bg-emerald/15 text-emerald border-emerald/30'
                : data.precursor.signal === 'HOLD'
                  ? 'bg-gold/15 text-gold border-gold/30'
                  : 'bg-crimson/15 text-crimson border-crimson/30'
            }`}>
              {data.precursor.signal}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div>
              <div className="text-sm uppercase tracking-wider text-stone-700">Precursor Score</div>
              <div className="text-lg font-mono tabular-nums font-bold text-amber-400">{data.precursor.precursor_score.toFixed(1)}</div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wider text-stone-700">Matched Episode</div>
              <div className="text-sm text-stone-300">{data.precursor.matched_episode}</div>
            </div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Matching Signals</div>
            <ul className="space-y-0.5">
              {data.precursor.matching_signals.map((s, i) => (
                <li key={i} className="text-sm text-stone-300">
                  <span className="text-amber-400 mr-1.5">&#9656;</span>{s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
